import React, { useEffect, useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { fetchSystemSettings, updateSystemSettings } from '../../api/mailCenter';

function looksBoolean(value) {
  return value === 'true' || value === 'false';
}

const SystemSettingsAdminPage = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadSettings = async (showSpinner = true) => {
    if (showSpinner) {
      setLoading(true);
    }
    setError('');
    try {
      const payload = await fetchSystemSettings();
      setSettings(Array.isArray(payload) ? payload : []);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger les paramètres système.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings(true);
  }, []);

  const updateLocalSetting = (key, nextValue) => {
    setSettings((currentSettings) => currentSettings.map((setting) => (
      setting.key === key ? { ...setting, value: nextValue } : setting
    )));
    setNotice('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const payload = await updateSystemSettings(settings.map((setting) => ({
        key: setting.key,
        value: setting.value,
        description: setting.description,
      })));
      setSettings(Array.isArray(payload) ? payload : []);
      setNotice('Paramètres enregistrés.');
    } catch (saveError) {
      setError(saveError.message || 'Impossible d’enregistrer les paramètres.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Paramètres" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Réglages système du Mail Center
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page centralise l’email administrateur, les digests et les bascules simples comme le mode coming soon exposé au frontend public.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <Button
                variant="outlined"
                onClick={() => loadSettings(false)}
                startIcon={<RefreshRoundedIcon />}
                sx={{ borderColor: 'rgba(247,243,234,0.35)', color: '#f7f3ea' }}
              >
                Recharger
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || loading}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 800, '&:hover': { bgcolor: '#e7b94f' } }}
              >
                Enregistrer
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success">{notice}</Alert> : null}

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff' }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#17323c', mb: 1.5 }} />
              <Typography variant="body2" sx={{ color: '#4f646f' }}>
                Chargement des paramètres système...
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.2}>
              {settings.map((setting) => (
                <Paper key={setting.key} variant="outlined" sx={{ p: 2.2, borderRadius: 4, borderColor: 'rgba(23,50,60,0.10)' }}>
                  <Stack spacing={1.4}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#17323c' }}>
                        {setting.key}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.4 }}>
                        {setting.description || 'Paramètre système EcotoneAI'}
                      </Typography>
                    </Box>

                    {looksBoolean(setting.value) ? (
                      <TextField
                        select
                        label="Valeur"
                        value={setting.value}
                        onChange={(event) => updateLocalSetting(setting.key, event.target.value)}
                        sx={{ maxWidth: 260 }}
                      >
                        <MenuItem value="true">Activé</MenuItem>
                        <MenuItem value="false">Désactivé</MenuItem>
                      </TextField>
                    ) : (
                      <TextField
                        label="Valeur"
                        value={setting.value || ''}
                        onChange={(event) => updateLocalSetting(setting.key, event.target.value)}
                        fullWidth
                      />
                    )}

                    <Typography variant="caption" sx={{ color: '#6b7c84' }}>
                      Dernière mise à jour: {setting.updatedAt ? new Date(setting.updatedAt).toLocaleString('fr-FR') : 'n/a'}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default SystemSettingsAdminPage;